// MeetingsScreen.jsx - SIMPLIFIED FILTERS
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Chip,
  alpha,
  Popover,
  useTheme,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  RemoveRedEyeRounded,
  ArrowBack,
  ArrowForward,
  FilterList,
  Clear,
  Today,
  Event,
  CalendarToday,
  ViewList,
  Edit,
  Assignment,
  Visibility,
  Phone,
  PhoneDisabled,
  PhoneCallback,
  PhoneForwarded,
  Search,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useThemeToggle } from "../context/ThemeContext";
import CustomAlert from "../components/customAlert";
import { url } from "../constants/url";

const MeetingsScreen = ({ userData }) => {
  const theme = useTheme();
  const { mode } = useThemeToggle();
  const navigate = useNavigate();

  const UID = userData?._id;
  const CID = userData?.companyAdded[0]?.companyId;
  const userRole = userData?.role;

  const [meetingsData, setMeetingsData] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [companies, setCompanies] = useState([]);

  // Filter states - SIMPLIFIED
  const [dateFilter, setDateFilter] = useState("today");
  const [statusFilter, setStatusFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all"); // upcoming, completed
  const [platformFilter, setPlatformFilter] = useState("all");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [tempFilters, setTempFilters] = useState({
    status: "all",
    company: "all",
    time: "all",
    platform: "all"
  });

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Edit dialog states
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [formData, setFormData] = useState({
    callStatus: "",
    leadStatus: "",
    dateTime: "",
    meetingPlatform: "",
    meetingLink: "",
    location: "",
    remarks: "",
    meetingRemarks: "",
  });

  // Alert states
  const [alert, setAlert] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // Status display mapping - SIMPLIFIED (format backend status)
  const statusDisplayMap = {
    meeting_scheduled: "Meeting Scheduled",
    meeting_completed: "Meeting Completed",
    meeting_cancelled: "Meeting Cancelled",
    meeting_rescheduled: "Meeting Rescheduled",
    hot: "Hot Lead",
    warm: "Warm Lead",
    cold: "Cold Lead",
    closed: "Closed",
    dead: "Dead Lead",
    call_not_received: "Call Not Received",
    received: "Call Received",
    switch_off: "Switch Off",
    wrong_number: "Wrong Number",
    quotation_shared: "Quotation Shared",
    quotation_approved: "Quotation Approved",
    quotation_rejected: "Quotation Rejected",
    negotiation: "Negotiation",
    "follow-up": "Follow Up",
    rejected: "Rejected",
    new: "New Lead"
  };

  // Platform display mapping
  const platformDisplayMap = {
    google_meet: "Google Meet",
    zoom: "Zoom",
    microsoft_teams: "Microsoft Teams",
    phone_call: "Phone Call",
    offline: "Offline Meeting",
    skype: "Skype",
    webex: "Webex",
    other: "Other Platform"
  };

  // Get display status text - FORMAT BACKEND STATUS
  const getDisplayStatus = (status) => {
    return statusDisplayMap[status] ||
      status?.charAt(0)?.toUpperCase() + status?.slice(1) ||
      "Unknown";
  };

  // Get display platform text
  const getDisplayPlatform = (platform) => {
    return platformDisplayMap[platform] ||
      platform?.replace(/_/g, " ")?.replace(/\b\w/g, (l) => l.toUpperCase()) ||
      "Not Specified";
  };

  // Status color mapping - KEEP ORIGINAL
  const getStatusColor = (status) => {
    if (status === "meeting_scheduled") return alpha(theme.palette.secondary.main, 0.2);
    if (status === "hot") return alpha(theme.palette.info.main, 0.3);
    if (status === "warm") return alpha(theme.palette.info.main, 0.3);
    if (status === "closed") return alpha(theme.palette.warning.main, 0.2);
    if (status === "dead") return alpha(theme.palette.grey[500], 0.2);
    return "inherit";
  };

  const getStatusChipColor = (status) => {
    if (status === "meeting_scheduled") return "secondary";
    if (status === "hot") return "info";
    if (status === "warm") return "info";
    if (status === "closed") return "warning";
    if (status === "dead") return "default";
    return "default";
  };

  // Helper functions - KEEP ORIGINAL
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatPlatform = (platform) => {
    if (!platform) return "";
    return platformDisplayMap[platform] || platform.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Format datetime for input - KEEP ORIGINAL
  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  // Convert to ISO string - KEEP ORIGINAL
  const convertToISOString = (datetimeLocalValue) => {
    if (!datetimeLocalValue) return "";
    try {
      const date = new Date(datetimeLocalValue);
      if (isNaN(date.getTime())) return "";
      return date.toISOString();
    } catch (error) {
      console.error("Error converting datetime to ISO:", error);
      return "";
    }
  };

  // Fetch meetings data - KEEP ORIGINAL
  const fetchMeetings = async () => {
    try {
      setLoading(true);

      let fetchUrl = `${url}/api/leads/fetch-meetings?userId=${UID}&role=${userRole}`;

      if (userRole === "admin" && CID) {
        fetchUrl += `&companyId=${CID}`;
      } else if (userRole === "superAdmin" && companyFilter !== "all") {
        fetchUrl += `&companyId=${companyFilter}`;
      }

      console.log("📅 Fetching meetings with URL:", fetchUrl);

      const response = await fetch(fetchUrl);
      const data = await response.json();

      if (data.data) {
        setMeetingsData(data.data);
        console.log("✅ Meetings fetched:", data.data.length);
        // Apply filters after fetching
        applyFilters(data.data);
      } else {
        setMeetingsData([]);
        setFilteredMeetings([]);
        console.log("❌ No meetings data received");
      }
    } catch (error) {
      console.error("❌ Error fetching meetings:", error);
      setMeetingsData([]);
      setFilteredMeetings([]);
      showAlert("error", "Failed to fetch meetings");
    } finally {
      setLoading(false);
    }
  };

  // SIMPLIFIED Filter application function
  const applyFilters = (meetings = meetingsData) => {
    console.log("🔄 Applying filters...", {
      totalMeetings: meetings.length,
      dateFilter,
      statusFilter,
      companyFilter,
      timeFilter,
      platformFilter
    });

    let filtered = [...meetings];

    // Apply status filter
    if (statusFilter !== "all") {
      console.log("🔍 Filtering by status:", statusFilter);
      filtered = filtered.filter(meeting => meeting.status === statusFilter);
    }

    // Apply company filter (superAdmin only)
    if (userRole === "superAdmin" && companyFilter !== "all") {
      console.log("🔍 Filtering by company:", companyFilter);
      filtered = filtered.filter(meeting =>
        meeting.company?._id === companyFilter ||
        meeting.companyId === companyFilter
      );
    }

    // Apply platform filter
    if (platformFilter !== "all") {
      console.log("🔍 Filtering by platform:", platformFilter);
      filtered = filtered.filter(meeting =>
        meeting.meeting?.platform === platformFilter
      );
    }

    // Apply time filter (upcoming/completed)
    if (timeFilter !== "all") {
      console.log("🔍 Filtering by time:", timeFilter);
      const now = new Date();

      filtered = filtered.filter(meeting => {
        if (!meeting.meeting?.dateTime) return false;

        const meetingDate = new Date(meeting.meeting.dateTime);

        if (timeFilter === "upcoming") {
          return meetingDate > now;
        } else if (timeFilter === "completed") {
          return meetingDate < now;
        }
        return true;
      });
    }

    // Apply date filter - KEEP ORIGINAL
    if (dateFilter !== "all") {
      console.log("🔍 Filtering by date:", dateFilter, selectedDate);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      filtered = filtered.filter(meeting => {
        if (!meeting.meeting?.dateTime) return false;

        const meetingDate = new Date(meeting.meeting.dateTime);
        meetingDate.setHours(0, 0, 0, 0);

        let dateMatch = false;

        switch (dateFilter) {
          case "today":
            dateMatch = meetingDate.getTime() === today.getTime();
            break;
          case "yesterday":
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            dateMatch = meetingDate.getTime() === yesterday.getTime();
            break;
          case "tomorrow":
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            dateMatch = meetingDate.getTime() === tomorrow.getTime();
            break;
          case "custom":
            if (!selectedDate) {
              dateMatch = true;
            } else {
              const customDate = new Date(selectedDate);
              customDate.setHours(0, 0, 0, 0);
              dateMatch = meetingDate.getTime() === customDate.getTime();
            }
            break;
          default:
            dateMatch = true;
        }

        return dateMatch;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(meeting => {
        const clientMatch = meeting.clientName && meeting.clientName.toLowerCase().includes(query);
        const contactMatch = meeting.contactNo && meeting.contactNo.includes(query);
        const remarksMatch = meeting.remarks && meeting.remarks.toLowerCase().includes(query);
        const meetingRemarksMatch = meeting.meeting?.remarks && meeting.meeting.remarks.toLowerCase().includes(query);
        const platformMatch = getDisplayPlatform(meeting.meeting?.platform).toLowerCase().includes(query);

        return clientMatch || contactMatch || remarksMatch || meetingRemarksMatch || platformMatch;
      });
    }

    console.log("🎯 Final filtered meetings:", filtered.length);
    setFilteredMeetings(filtered);
  };

  // Fetch companies for superAdmin - KEEP ORIGINAL
  const fetchCompanies = async () => {
    if (userRole !== "superAdmin") return;
    try {
      const response = await fetch(`${url}/api/company/fetch-companies`);
      const data = await response.json();
      if (data.data) {
        setCompanies(data.data);
      }
    } catch (error) {
      console.error("❌ Error fetching companies:", error);
      showAlert("error", "Failed to fetch companies");
    }
  };

  // Calculate meeting statistics - KEEP ORIGINAL
  const meetingStats = {
    total: filteredMeetings.length,
    today: filteredMeetings.filter(m => {
      const meetingDate = new Date(m.meeting?.dateTime);
      const today = new Date();
      return meetingDate.toDateString() === today.toDateString();
    }).length,
    upcoming: filteredMeetings.filter(m => new Date(m.meeting?.dateTime) > new Date()).length,
    completed: filteredMeetings.filter(m => new Date(m.meeting?.dateTime) < new Date()).length,
  };

  // Show alert function - KEEP ORIGINAL
  const showAlert = (severity, message) => {
    setAlert({
      open: true,
      severity,
      message,
    });
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, severity: "success", message: "" });
  };

  // Edit dialog handlers - KEEP ORIGINAL
  const handleOpenUpdateDialog = (meeting) => {
    setSelectedLead(meeting);

    const existingMeeting = meeting.meeting || {};
    const existingDateTime = formatDateTimeForInput(existingMeeting.dateTime);

    setFormData({
      callStatus: meeting.callStatus || "",
      leadStatus: meeting.leadStatus || "",
      dateTime: existingDateTime || "",
      meetingPlatform: existingMeeting.platform || "",
      meetingLink: existingMeeting.link || "",
      location: existingMeeting.location || "",
      remarks: "",
      meetingRemarks: existingMeeting.remarks || "",
    });
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
    setSelectedLead(null);
    setFormData({
      callStatus: "",
      leadStatus: "",
      dateTime: "",
      meetingPlatform: "",
      meetingLink: "",
      location: "",
      remarks: "",
      meetingRemarks: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Update lead status - KEEP ORIGINAL
  const handleUpdateLeadStatus = async () => {
    try {
      if (!selectedLead) return;

      setLoading(true);

      const isoDateTime = convertToISOString(formData.dateTime);

      const payload = {
        leadId: selectedLead._id,
        userId: UID,
        callStatus: formData.callStatus,
        leadStatus: formData.leadStatus,
        dateTime: isoDateTime,
        meetingPlatform: formData.meetingPlatform,
        meetingLink: formData.meetingLink,
        location: formData.location,
        remarks: formData.remarks,
        meetingRemarks: formData.meetingRemarks,
        companyId: selectedLead.company?._id || CID,
      };

      const updateRes = await fetch(`${url}/api/leads/update-lead-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const updateData = await updateRes.json();
      if (!updateRes.ok) {
        throw new Error(updateData.message || "Failed to update meeting");
      }

      showAlert("success", "Meeting updated successfully");
      await fetchMeetings();
      handleCloseUpdateDialog();
    } catch (error) {
      console.error("❌ Error updating meeting:", error);
      showAlert("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  // SIMPLIFIED Filter handlers
  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    if (filter === "today") {
      setSelectedDate(new Date().toISOString().split("T")[0]);
    } else if (filter === "yesterday") {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      setSelectedDate(d.toISOString().split("T")[0]);
    } else if (filter === "tomorrow") {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      setSelectedDate(d.toISOString().split("T")[0]);
    }
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
  };

  const handleCompanyFilterChange = (company) => {
    setCompanyFilter(company);
  };

  const handleTimeFilterChange = (time) => {
    setTimeFilter(time);
  };

  const handlePlatformFilterChange = (platform) => {
    setPlatformFilter(platform);
  };

  // Filter popover handlers - UPDATED with simple filters
  const handleOpenFilterPopover = (event) => {
    setTempFilters({
      status: statusFilter,
      company: companyFilter,
      time: timeFilter,
      platform: platformFilter
    });
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const handleApplyFilters = () => {
    setStatusFilter(tempFilters.status);
    setCompanyFilter(tempFilters.company);
    setTimeFilter(tempFilters.time);
    setPlatformFilter(tempFilters.platform);
    handleCloseFilterPopover();
  };

  const handleResetFilters = () => {
    setTempFilters({
      status: "all",
      company: "all",
      time: "all",
      platform: "all"
    });
    setStatusFilter("all");
    setCompanyFilter("all");
    setTimeFilter("all");
    setPlatformFilter("all");
    handleCloseFilterPopover();
  };

  const handleClearFilters = () => {
    setDateFilter("all");
    setStatusFilter("all");
    setCompanyFilter("all");
    setTimeFilter("all");
    setPlatformFilter("all");
    setSelectedDate("");
  };

  // Clear individual filter
  const handleClearFilter = (filterType) => {
    switch (filterType) {
      case 'date':
        setDateFilter("all");
        setSelectedDate("");
        break;
      case 'status':
        setStatusFilter("all");
        break;
      case 'company':
        setCompanyFilter("all");
        break;
      case 'time':
        setTimeFilter("all");
        break;
      case 'platform':
        setPlatformFilter("all");
        break;
      default:
        break;
    }
  };

  // Apply filters when filter states change
  useEffect(() => {
    if (meetingsData.length > 0) {
      applyFilters(meetingsData);
    }
  }, [dateFilter, statusFilter, companyFilter, timeFilter, platformFilter, selectedDate, searchQuery, meetingsData]);

  useEffect(() => {
    fetchMeetings();
    if (userRole === "superAdmin") {
      fetchCompanies();
    }
  }, [UID, userRole, CID]);

  const primaryColor = theme.palette.primary.main;
  const redcolor = theme.palette.error.main;
  const greencolor = theme.palette.success.main;

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          bgcolor: "background.default",
          color: "text.primary",
          // p: 3,
          // ml: -3, // Unified margin
          overflow: "hidden",
          // mt: -2.5, // Unified top margin
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Fixed Header Section - KEEP ORIGINAL UI */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
            bgcolor: "background.default",
            pb: 2,
            borderBottom: `1px solid ${alpha(primaryColor, 0.1)}`,
            mb: 2,
          }}
        >
          {/* Top Row - Filter and Active Filters */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
            }}
          >
            {/* Left Side - Filter Button and Active Filters */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
              <Button
                variant="contained"
                startIcon={<FilterList />}
                onClick={handleOpenFilterPopover}
                sx={{
                  bgcolor: "transparent",
                  boxShadow: "none",
                  border: "none",
                  color: primaryColor,
                  "&:hover": {
                    bgcolor: alpha(primaryColor, 0.08),
                    boxShadow: "none",
                    border: "none",
                  },
                  minWidth: "auto",
                  px: 2.3,
                  height: "38px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "15px",
                }}
              >
                FILTERS
              </Button>

              {/* Active Filters Chips - UPDATED with simple filters */}
              {(companyFilter !== "all" || dateFilter !== "all" || statusFilter !== "all" || timeFilter !== "all" || platformFilter !== "all") && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  {/* Date Filter Chip */}
                  {dateFilter !== "all" && (
                    <Chip
                      icon={
                        dateFilter === "custom" ? <Event /> :
                          dateFilter === "today" ? <Today /> :
                            dateFilter === "yesterday" ? <ArrowBack /> :
                              dateFilter === "tomorrow" ? <ArrowForward /> :
                                <ViewList />
                      }
                      label={
                        dateFilter === "custom" ? `Custom: ${selectedDate}` :
                          dateFilter === "today" ? "Today" :
                            dateFilter === "yesterday" ? "Yesterday" :
                              dateFilter === "tomorrow" ? "Tomorrow" :
                                "All Meetings"
                      }
                      size="medium"
                      onDelete={() => handleClearFilter('date')}
                      sx={{ color: "success.main", fontWeight: 700, height: "32px" }}
                    />
                  )}

                  {/* Status Filter Chip */}
                  {statusFilter !== "all" && (
                    <Chip
                      label={`Status: ${getDisplayStatus(statusFilter)}`}
                      size="medium"
                      onDelete={() => handleClearFilter('status')}
                      sx={{ color: "success.main", fontWeight: 700, height: "32px" }}
                    />
                  )}

                  {/* Time Filter Chip */}
                  {timeFilter !== "all" && (
                    <Chip
                      label={`Time: ${timeFilter === 'upcoming' ? 'Upcoming' : 'Completed'}`}
                      size="medium"
                      onDelete={() => handleClearFilter('time')}
                      sx={{ color: "info.main", fontWeight: 700, height: "32px" }}
                    />
                  )}

                  {/* Platform Filter Chip */}
                  {platformFilter !== "all" && (
                    <Chip
                      label={`Platform: ${getDisplayPlatform(platformFilter)}`}
                      size="medium"
                      onDelete={() => handleClearFilter('platform')}
                      sx={{ color: "warning.main", fontWeight: 700, height: "32px" }}
                    />
                  )}

                  {/* Company Filter Chip */}
                  {companyFilter !== "all" && userRole === "superAdmin" && (
                    <Chip
                      label={`Company: ${companies.find((c) => c._id === companyFilter)?.companyName ||
                        companies.find((c) => c._id === companyFilter)?.name ||
                        "Unknown"
                        }`}
                      size="medium"
                      onDelete={() => handleClearFilter('company')}
                      sx={{ color: "success.main", fontWeight: 700, height: "32px" }}
                    />
                  )}

                  {/* Clear All Filters */}
                  <Button
                    size="small"
                    startIcon={<Clear />}
                    onClick={handleClearFilters}
                    sx={{
                      color: "error.main",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                      height: "32px",
                    }}
                  >
                    Clear All
                  </Button>
                </Box>
              )}
            </Box>

            {/* Right Side - Search Bar */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <TextField
                placeholder="Search meetings..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  width: "250px",
                  "& .MuiInputBase-root": {
                    height: 38,
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 8px",
                    fontSize: "0.875rem",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18, color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                        sx={{ p: 0.5 }}
                      >
                        <Clear sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          {/* Second Row - Stats and Date Filters - KEEP ORIGINAL UI */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Left Side - Meeting Statistics */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <Chip label={`Total: ${meetingStats.total}`} variant="outlined" color="primary" size="medium" sx={{ fontWeight: "bold", height: "32px" }} />
              <Chip label={`Today: ${meetingStats.today}`} variant="filled" color="info" size="medium" sx={{ fontWeight: "bold", height: "32px" }} />
              <Chip label={`Upcoming: ${meetingStats.upcoming}`} variant="filled" color="success" size="medium" sx={{ fontWeight: "bold", height: "32px" }} />
              <Chip label={`Completed: ${meetingStats.completed}`} variant="filled" color="warning" size="medium" sx={{ fontWeight: "bold", height: "32px" }} />
            </Box>

            {/* Right Side - Date Filters */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              {[
                { value: "all", label: "All Meetings", icon: <ViewList /> },
                { value: "yesterday", label: "Yesterday", icon: <ArrowBack /> },
                { value: "today", label: "Today", icon: <Today /> },
                { value: "tomorrow", label: "Tomorrow", icon: <ArrowForward /> },
              ].map((filter) => (
                <Chip
                  key={filter.value}
                  icon={filter.icon}
                  label={filter.label}
                  variant={dateFilter === filter.value ? "filled" : "outlined"}
                  color={dateFilter === filter.value ? "primary" : "default"}
                  onClick={() => handleDateFilterChange(filter.value)}
                  size="medium"
                  sx={{
                    height: "32px",
                    ...(dateFilter === filter.value && {
                      bgcolor: "primary.main",
                      "&:hover": { bgcolor: alpha(primaryColor, 0.9) },
                    }),
                  }}
                />
              ))}

              {/* Date Picker */}
              <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                <TextField
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setDateFilter("custom");
                  }}
                  sx={{
                    width: "130px",
                    "& .MuiInputBase-root": {
                      height: 40,
                      borderRadius: 1,
                      border: `1px solid ${alpha(primaryColor, 0.3)}`,
                      "&:hover": {
                        borderColor: primaryColor,
                      },
                      "&.Mui-focused": {
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 2px ${alpha(primaryColor, 0.2)}`,
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiInputBase-input": {
                      padding: "8px 12px",
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Scrollable Content Area - KEEP ORIGINAL UI */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            "& ::-webkit-scrollbar": { display: "none" },
            "&": { "-ms-overflow-style": "none", "scrollbar-width": "none" },
          }}
        >
          {/* Meetings Table */}
          <Card
            sx={{
              bgcolor: "background.paper",
              border: `2px solid ${alpha(primaryColor, 0.2)}`,
              borderRadius: 2,
              boxShadow: "0 4px 12px 0 rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{
              p: 0,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              "&:last-child": { pb: 0 }
            }}>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4} flex={1}>
                  <CircularProgress sx={{ color: "primary.main" }} />
                </Box>
              ) : filteredMeetings.length === 0 ? (
                <Typography variant="h6" textAlign="center" py={4} color="text.secondary" flex={1}>
                  No meetings found matching your filters
                </Typography>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    borderRadius: 0,
                    boxShadow: "none",
                    "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
                      borderBottom: `1px solid ${alpha(primaryColor, 0.1)}`,
                    },
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {userRole === "superAdmin" && (
                          <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                            Company
                          </TableCell>
                        )}
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                          Client Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                          Contact No
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                          Meeting Date & Time
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                          Platform
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                          Meeting Link/Location
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredMeetings.map((meeting) => (
                        <TableRow
                          key={meeting._id}
                          sx={{
                            backgroundColor: getStatusColor(meeting.status),
                            "&:hover": {
                              backgroundColor: `${getStatusColor(meeting.status)}DD`,
                            },
                            borderBottom: `1px solid ${alpha(primaryColor, 0.1)}`,
                          }}
                        >
                          {userRole === "superAdmin" && (
                            <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                              {meeting.company?.companyName || meeting.company?.name || "N/A"}
                            </TableCell>
                          )}
                          <TableCell sx={{ fontWeight: "bold", borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                            {meeting.clientName}
                          </TableCell>
                          <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                            {meeting.contactNo}
                          </TableCell>
                          <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                {formatDate(meeting.meeting?.dateTime)}
                              </Typography>
                              <Typography variant="body2" sx={{ color: "primary.main" }}>
                                {formatTime(meeting.meeting?.dateTime)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                            {formatPlatform(meeting.meeting?.platform)}
                          </TableCell>
                          <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                            {meeting.meeting?.platform === "offline"
                              ? meeting.meeting?.location
                              : meeting.meeting?.link
                            }
                          </TableCell>
                          <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                            <Chip
                              label={getDisplayStatus(meeting.status)}
                              size="small"
                              color={getStatusChipColor(meeting.status)}
                              sx={{ fontWeight: "bold" }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/lead/${meeting._id}`)}
                                sx={{ color: redcolor, "&:hover": { backgroundColor: alpha(redcolor, 0.1) } }}
                                title="View Records"
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenUpdateDialog(meeting)}
                                sx={{ color: greencolor, "&:hover": { backgroundColor: alpha(greencolor, 0.1) } }}
                                title="Update Meeting"
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* SIMPLIFIED Filter Popover */}
        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilterPopover}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              p: 2,
              minWidth: 300,
              borderRadius: 2,
              border: `2px solid ${alpha(primaryColor, 0.1)}`,
              bgcolor: "background.paper",
            },
          }}
        >
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Time Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Time</InputLabel>
              <Select
                value={tempFilters.time}
                label="Time"
                onChange={(e) => setTempFilters({ ...tempFilters, time: e.target.value })}
              >
                <MenuItem value="all">All Meetings</MenuItem>
                <MenuItem value="upcoming">Upcoming Meetings</MenuItem>
                <MenuItem value="completed">Completed Meetings</MenuItem>
              </Select>
            </FormControl>

            {/* Platform Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Platform</InputLabel>
              <Select
                value={tempFilters.platform}
                label="Platform"
                onChange={(e) => setTempFilters({ ...tempFilters, platform: e.target.value })}
              >
                <MenuItem value="all">All Platforms</MenuItem>
                <MenuItem value="google_meet">Google Meet</MenuItem>
                <MenuItem value="zoom">Zoom</MenuItem>
                <MenuItem value="microsoft_teams">Microsoft Teams</MenuItem>
                <MenuItem value="phone_call">Phone Call</MenuItem>
                <MenuItem value="offline">Offline Meeting</MenuItem>
              </Select>
            </FormControl>

            {userRole === "superAdmin" && (
              <FormControl fullWidth size="small">
                <InputLabel>Company</InputLabel>
                <Select
                  value={tempFilters.company}
                  label="Company"
                  onChange={(e) => setTempFilters({ ...tempFilters, company: e.target.value })}
                >
                  <MenuItem value="all">All Companies</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company._id} value={company._id}>
                      {company.companyName || company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
              <Button onClick={handleResetFilters} color="inherit">
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                sx={{
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: alpha(primaryColor, 0.9) },
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Edit Meeting Dialog */}
        <Dialog
          open={openUpdateDialog}
          onClose={handleCloseUpdateDialog}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ fontWeight: "bold", borderBottom: 1, borderColor: "divider" }}>
            Update Meeting Details
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Meeting Status</InputLabel>
                <Select
                  name="leadStatus"
                  value={formData.leadStatus}
                  onChange={handleChange}
                  label="Meeting Status"
                >
                  <MenuItem value="meeting_scheduled">Meeting Scheduled</MenuItem>
                  <MenuItem value="meeting_completed">Meeting Completed</MenuItem>
                  <MenuItem value="rescheduled">Rescheduled Call</MenuItem>
                  <MenuItem value="dead">Cancelled/Dead Lead</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Meeting Platform</InputLabel>
                <Select
                  name="meetingPlatform"
                  value={formData.meetingPlatform}
                  onChange={handleChange}
                  label="Meeting Platform"
                >
                  <MenuItem value="google_meet">Google Meet</MenuItem>
                  <MenuItem value="zoom">Zoom</MenuItem>
                  <MenuItem value="microsoft_teams">Microsoft Teams</MenuItem>
                  <MenuItem value="phone_call">Phone Call</MenuItem>
                  <MenuItem value="offline">Offline Meeting</MenuItem>
                </Select>
              </FormControl>

              {formData.meetingPlatform === "offline" ? (
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  size="small"
                  placeholder="Enter office/venue address"
                />
              ) : (
                <TextField
                  fullWidth
                  label="Meeting Link"
                  name="meetingLink"
                  value={formData.meetingLink}
                  onChange={handleChange}
                  size="small"
                  placeholder="Paste meeting URL here"
                />
              )}

              <TextField
                fullWidth
                label="Meeting Date & Time"
                type="datetime-local"
                name="dateTime"
                value={formData.dateTime}
                onChange={handleChange}
                size="small"
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Meeting Remarks"
                name="meetingRemarks"
                value={formData.meetingRemarks}
                onChange={handleChange}
                multiline
                rows={2}
                size="small"
                placeholder="Specific notes for this meeting..."
              />

              <TextField
                fullWidth
                label="Overall Remarks (History)"
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                multiline
                rows={2}
                size="small"
                placeholder="Key notes to add to lead history..."
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            <Button onClick={handleCloseUpdateDialog} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdateLeadStatus}
              disabled={loading}
              sx={{ px: 3 }}
            >
              {loading ? "Updating..." : "Update Meeting"}
            </Button>
          </DialogActions>
        </Dialog>

        <CustomAlert
          open={alert.open}
          severity={alert.severity}
          message={alert.message}
          onClose={handleCloseAlert}
        />
      </Box>
    </>
  );
};

export default MeetingsScreen;
